import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  Shield, ShieldCheck, Lock, Gavel, Clock, ArrowRight, 
  CheckCircle, ChevronDown, ChevronUp, X, Globe,
  Eye, FileText, Users, Building2, ArrowUpRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import SEO, { 
  organizationSchema, 
  softwareApplicationSchema, 
  createFAQSchema
} from "../components/SEO";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// FAQ Data - Bilingual (B2B focused)
const faqDataPT = [
  {
    question: "Minha empresa precisa ter canal de denúncias e ouvidoria?",
    answer: "Embora não seja obrigatório para todas as empresas, ter um canal de denúncias e ouvidoria é uma prática recomendada de governança corporativa. Ajuda a identificar problemas internos antes que se tornem crises, demonstra compromisso com a ética e pode ser exigido por parceiros comerciais, certificações ou processos de compliance."
  },
  {
    question: "As denúncias são realmente anônimas?",
    answer: "Sim, 100% anônimas. O sistema não coleta nenhum dado que possa identificar o denunciante. Não armazenamos IP, localização ou qualquer informação pessoal. O denunciante recebe apenas um protocolo e senha para acompanhar o caso de forma segura."
  },
  {
    question: "A ouvidoria serve apenas para reclamações?",
    answer: "Não. A ouvidoria é um canal para diversos tipos de manifestações: sugestões de melhoria, reclamações, elogios a colaboradores e solicitações de informação. É uma ferramenta completa de comunicação entre a empresa e seus públicos."
  },
  {
    question: "Preciso de equipe jurídica ou de compliance para usar?",
    answer: "Não. O OmbudNow foi criado pensando em pequenas e médias empresas que não possuem departamentos especializados. A plataforma é intuitiva e pode ser gerenciada por qualquer pessoa da área administrativa, RH ou gestão."
  },
  {
    question: "Quem pode acessar denúncias e atendimentos?",
    answer: "Apenas os usuários autorizados pela sua empresa têm acesso ao painel administrativo. Você define quem são os gestores e investigadores. Todas as ações são registradas para garantir rastreabilidade e segurança."
  }
];

const faqDataEN = [
  {
    question: "Does my company need a whistleblowing and ombudsman channel?",
    answer: "Although not mandatory for all companies, having a whistleblowing and ombudsman channel is a recommended corporate governance practice. It helps identify internal issues before they become crises, demonstrates commitment to ethics, and may be required by business partners, certifications, or compliance processes."
  },
  {
    question: "Are reports really anonymous?",
    answer: "Yes, 100% anonymous. The system does not collect any data that could identify the reporter. We do not store IP, location, or any personal information. The reporter only receives a protocol and password to securely track the case."
  },
  {
    question: "Is the ombudsman only for complaints?",
    answer: "No. The ombudsman is a channel for various types of requests: improvement suggestions, complaints, compliments to employees, and information requests. It is a complete communication tool between the company and its stakeholders."
  },
  {
    question: "Do I need a legal or compliance team to use it?",
    answer: "No. OmbudNow was created with small and medium companies in mind that don't have specialized departments. The platform is intuitive and can be managed by anyone in administration, HR, or management."
  },
  {
    question: "Who can access reports and cases?",
    answer: "Only users authorized by your company have access to the admin panel. You define who the managers and investigators are. All actions are logged to ensure traceability and security."
  }
];

// B2B Content translations
const content = {
  pt: {
    hero: {
      overline: "CANAL DE ÉTICA CORPORATIVO",
      title: "Canal de Denúncias e Ouvidoria",
      titleAccent: "pronto para sua empresa",
      subtitle: "Crie um canal de denúncias anônimo e uma ouvidoria corporativa em poucos minutos. Ideal para pequenas e médias empresas que precisam estruturar comunicação interna e adotar boas práticas sem burocracia.",
      cta: "Começar agora",
      ctaSecondary: "Falar com especialista"
    },
    stats: [
      { value: "500+", label: "Empresas ativas" },
      { value: "15k+", label: "Casos gerenciados" },
      { value: "99.9%", label: "Uptime garantido" },
      { value: "<5min", label: "Tempo de setup" }
    ],
    compliance: {
      overline: "CONFORMIDADE LEGAL",
      title: "100% adequado à legislação brasileira",
      subtitle: "Atenda às exigências legais de forma rápida e segura. Estruturado para auditorias e fiscalizações.",
      items: [
        {
          title: "CIPA & NR-1",
          description: "Canal de denúncias aderente às normas regulamentadoras de segurança do trabalho.",
          icon: "shield"
        },
        {
          title: "Lei 14.611/2023",
          description: "Adequação completa às exigências de prevenção de assédio moral e sexual no trabalho.",
          icon: "gavel"
        },
        {
          title: "LGPD",
          description: "Garantia total de anonimato com aderência à Lei Geral de Proteção de Dados.",
          icon: "lock"
        },
        {
          title: "ISO 27001",
          description: "Padrões internacionais de segurança da informação aplicados em toda plataforma.",
          icon: "check"
        }
      ]
    },
    features: {
      overline: "POR QUE OMBUDNOW",
      title: "Governança corporativa sem complicação",
      items: [
        { 
          title: "Previna crises", 
          description: "Identifique problemas antes que se tornem escândalos públicos ou processos trabalhistas.",
          icon: "eye"
        },
        { 
          title: "Atenda a legislação", 
          description: "Esteja preparado para auditorias, certificações e exigências de parceiros comerciais.",
          icon: "file"
        },
        { 
          title: "Fortaleça a cultura", 
          description: "Demonstre compromisso com ética e transparência para colaboradores e stakeholders.",
          icon: "users"
        }
      ]
    },
    trust: {
      title: "Recursos enterprise para PMEs",
      features: [
        "Denúncias 100% anônimas ou identificadas",
        "Ouvidoria estruturada com controle de prazos",
        "SLA configurável e alertas automáticos",
        "Múltiplos investigadores e gestores",
        "Dados criptografados e protegidos",
        "Relatórios e exportação de dados"
      ]
    },
    pricing: {
      overline: "PLANOS",
      title: "Escolha o plano ideal para sua empresa",
      subtitle: "Sem taxa de adesão. Cancele quando quiser.",
      plans: [
        {
          name: "Gratuito",
          price: "R$ 0",
          period: "/mês",
          description: "Para conhecer a plataforma",
          features: ["1 usuário administrador", "3 registros/mês", "Canal de denúncias básico", "Suporte via documentação"],
          cta: "Começar grátis",
          popular: false,
          enterprise: false
        },
        {
          name: "Essencial",
          price: "R$ 89",
          period: "/mês",
          description: "Empresas com 1–10 colaboradores",
          features: ["7 dias grátis", "5 registros/mês", "Dashboard completo", "Suporte via email"],
          cta: "Teste grátis 7 dias",
          popular: false,
          enterprise: false
        },
        {
          name: "Profissional",
          price: "R$ 199",
          period: "/mês",
          description: "Empresas com 11–100 colaboradores",
          features: ["7 dias grátis", "20 registros/mês", "Múltiplos investigadores", "Relatórios completos", "Suporte prioritário"],
          cta: "Teste grátis 7 dias",
          popular: true,
          enterprise: false
        },
        {
          name: "Empresarial",
          price: "R$ 499",
          period: "/mês",
          description: "Empresas com +100 colaboradores",
          features: ["7 dias grátis", "Registros ilimitados", "Suporte dedicado", "Consultoria inclusa", "0800 opcional", "SLA personalizado"],
          cta: "Falar com vendas",
          popular: false,
          enterprise: true
        }
      ]
    },
    faq: {
      title: "Perguntas frequentes"
    },
    cta: {
      title: "Pronto para estruturar seu canal de ética?",
      subtitle: "Comece em menos de 5 minutos. Sem burocracia.",
      button: "Criar meu canal gratuito"
    },
    nav: {
      about: "Sobre",
      blog: "Blog",
      contact: "Contato",
      access: "Acessar",
      create: "Começar grátis"
    },
    modal: {
      access: {
        title: "Acessar meu canal",
        subtitle: "Digite o identificador da sua empresa",
        placeholder: "ex. minha-empresa",
        submit: "Acessar",
        accessing: "Acessando..."
      }
    },
    footer: {
      cta: "Vamos conversar?",
      ctaSubtitle: "Tire suas dúvidas com nosso time comercial",
      ctaButton: "Agendar demonstração",
      rights: "Todos os direitos reservados.",
      privacy: "Privacidade",
      terms: "Termos"
    }
  },
  en: {
    hero: {
      overline: "CORPORATE ETHICS CHANNEL",
      title: "Whistleblowing and Ombudsman Channel",
      titleAccent: "ready for your company",
      subtitle: "Create an anonymous whistleblowing channel and corporate ombudsman in minutes. Ideal for small and medium companies that need to structure internal communication and adopt best practices without bureaucracy.",
      cta: "Get started",
      ctaSecondary: "Talk to sales"
    },
    stats: [
      { value: "500+", label: "Active companies" },
      { value: "15k+", label: "Cases managed" },
      { value: "99.9%", label: "Guaranteed uptime" },
      { value: "<5min", label: "Setup time" }
    ],
    compliance: {
      overline: "LEGAL COMPLIANCE",
      title: "Enterprise-grade compliance & security",
      subtitle: "Meet regulatory requirements quickly and securely. Structured for audits and inspections.",
      items: [
        {
          title: "SOX & Dodd-Frank",
          description: "Fully compliant with whistleblower protection laws and reporting requirements.",
          icon: "shield"
        },
        {
          title: "EU Directive",
          description: "Complete adequacy to the European Whistleblower Protection Directive.",
          icon: "gavel"
        },
        {
          title: "GDPR Ready",
          description: "Full anonymity guarantee with adherence to data protection regulations.",
          icon: "lock"
        },
        {
          title: "ISO 27001",
          description: "International information security standards applied across the platform.",
          icon: "check"
        }
      ]
    },
    features: {
      overline: "WHY OMBUDNOW",
      title: "Corporate governance without complexity",
      items: [
        { 
          title: "Prevent crises", 
          description: "Identify problems before they become public scandals or lawsuits.",
          icon: "eye"
        },
        { 
          title: "Meet regulations", 
          description: "Be prepared for audits, certifications and business partner requirements.",
          icon: "file"
        },
        { 
          title: "Strengthen culture", 
          description: "Demonstrate commitment to ethics and transparency to employees and stakeholders.",
          icon: "users"
        }
      ]
    },
    trust: {
      title: "Enterprise features for SMBs",
      features: [
        "100% anonymous or identified reports",
        "Structured ombudsman with deadline control",
        "Configurable SLA and automatic alerts",
        "Multiple investigators and managers",
        "Encrypted and protected data",
        "Reports and data export"
      ]
    },
    pricing: {
      overline: "PRICING",
      title: "Choose the ideal plan for your company",
      subtitle: "No setup fee. Cancel anytime.",
      plans: [
        {
          name: "Free",
          price: "US$ 0",
          period: "/month",
          description: "To explore the platform",
          features: ["1 admin user", "3 reports/month", "Basic whistleblowing channel", "Documentation support"],
          cta: "Start for free",
          popular: false,
          enterprise: false
        },
        {
          name: "Essential",
          price: "US$ 29",
          period: "/month",
          description: "Companies with 1–10 employees",
          features: ["7-day free trial", "5 reports/month", "Full dashboard", "Email support"],
          cta: "Try free for 7 days",
          popular: false,
          enterprise: false
        },
        {
          name: "Professional",
          price: "US$ 49",
          period: "/month",
          description: "Companies with 11–100 employees",
          features: ["7-day free trial", "20 reports/month", "Multiple investigators", "Full reports", "Priority support"],
          cta: "Try free for 7 days",
          popular: true,
          enterprise: false
        },
        {
          name: "Enterprise",
          price: "US$ 129",
          period: "/month",
          description: "Companies with 100+ employees",
          features: ["7-day free trial", "Unlimited reports", "Dedicated support", "Consulting included", "Toll-free optional", "Custom SLA"],
          cta: "Contact sales",
          popular: false,
          enterprise: true
        }
      ]
    },
    faq: {
      title: "Frequently asked questions"
    },
    cta: {
      title: "Ready to structure your ethics channel?",
      subtitle: "Start in less than 5 minutes. No bureaucracy.",
      button: "Create my free channel"
    },
    nav: {
      about: "About",
      blog: "Blog",
      contact: "Contact",
      access: "Access",
      create: "Start free"
    },
    modal: {
      access: {
        title: "Access my channel",
        subtitle: "Enter your company identifier",
        placeholder: "e.g. my-company",
        submit: "Access",
        accessing: "Accessing..."
      }
    },
    footer: {
      cta: "Let's talk?",
      ctaSubtitle: "Get your questions answered by our sales team",
      ctaButton: "Schedule demo",
      rights: "All rights reserved.",
      privacy: "Privacy",
      terms: "Terms"
    }
  }
};

export default function SaaSLanding() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [showAccess, setShowAccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accessSlug, setAccessSlug] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const currentLang = i18n.language || 'pt';
  const t = content[currentLang] || content.pt;
  const faqData = currentLang === 'en' ? faqDataEN : faqDataPT;

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleCheckout = async (planIndex) => {
    // Plan IDs based on language - matching backend PLANS_CONFIG
    const planIdsPT = ['free', 'essencial_brl', 'profissional_brl', 'empresarial_brl'];
    const planIdsEN = ['free', 'essential_usd', 'professional_usd', 'enterprise_usd'];
    const planIds = currentLang === 'en' ? planIdsEN : planIdsPT;
    const packageId = planIds[planIndex];
    
    if (packageId === 'free') {
      navigate('/onboarding?plan=free');
      return;
    }
    
    if (packageId === 'empresarial_brl' || packageId === 'enterprise_usd') {
      navigate(currentLang === 'en' ? '/en/contact' : '/pt/contato');
      return;
    }
    
    setCheckoutLoading(planIndex);
    try {
      const response = await axios.post(`${API_URL}/stripe/checkout`, {
        plan_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(currentLang === 'en' ? 'Error starting checkout' : 'Erro ao iniciar checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleAccess = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const slug = accessSlug.toLowerCase().trim();
      const res = await axios.get(`${API_URL}/empresas/${slug}/check`);
      
      if (res.data.exists) {
        navigate(`/${slug}`);
      } else {
        toast.error(currentLang === 'en' ? "Company not found" : "Empresa não encontrada");
      }
    } catch (error) {
      toast.error(currentLang === 'en' ? "Company not found" : "Empresa não encontrada");
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const icons = { 
      shield: ShieldCheck, 
      lock: Lock, 
      check: CheckCircle, 
      gavel: Gavel,
      eye: Eye,
      file: FileText,
      users: Users
    };
    return icons[iconName] || Shield;
  };

  // SEO
  const seoConfig = currentLang === 'en' ? {
    title: "Whistleblowing & Ombudsman Channel for Companies | OmbudNow",
    description: "Create an anonymous whistleblowing channel and corporate ombudsman in minutes. Enterprise-grade compliance for SMBs.",
    canonical: "/en",
    keywords: "whistleblowing channel, ombudsman, corporate compliance, anonymous reporting"
  } : {
    title: "Canal de Denúncias e Ouvidoria para Empresas | OmbudNow",
    description: "Crie um canal de denúncias anônimo e ouvidoria corporativa em minutos. Compliance enterprise para PMEs.",
    canonical: "/pt",
    keywords: "canal de denúncias, ouvidoria corporativa, compliance, LGPD, CIPA"
  };

  const faqSchemaData = createFAQSchema(faqData.map(f => ({ question: f.question, answer: f.answer })));

  return (
    <div className="min-h-screen bg-white font-ibm">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        lang={currentLang}
        keywords={seoConfig.keywords}
        schemas={[organizationSchema, softwareApplicationSchema, faqSchemaData]}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-9 h-9 bg-slate-900 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-slate-900 font-chivo tracking-tight">OMBUDNOW</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to={currentLang === 'en' ? '/en/about' : '/pt/sobre'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {t.nav.about}
              </Link>
              <Link to={currentLang === 'en' ? '/en/blog' : '/pt/blog'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {t.nav.blog}
              </Link>
              <Link to={currentLang === 'en' ? '/en/contact' : '/pt/contato'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {t.nav.contact}
              </Link>
              <div className="flex items-center gap-1.5 text-slate-500 text-sm border-l border-slate-200 pl-6 ml-2">
                <Globe className="w-4 h-4" />
                <button 
                  onClick={() => changeLanguage('pt')} 
                  className={`px-1 ${currentLang === 'pt' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                >
                  PT
                </button>
                <span className="text-slate-300">/</span>
                <button 
                  onClick={() => changeLanguage('en')} 
                  className={`px-1 ${currentLang === 'en' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                >
                  EN
                </button>
              </div>
            </nav>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium"
                onClick={() => setShowAccess(true)}
                data-testid="btn-access"
              >
                {t.nav.access}
              </Button>
              <Button
                className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-5"
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="btn-create"
              >
                {t.nav.create}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Content */}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4" data-testid="hero-overline">
                  {t.hero.overline}
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tighter leading-none mb-6 font-chivo" data-testid="hero-title">
                  {t.hero.title}
                  <span className="block text-blue-600">{t.hero.titleAccent}</span>
                </h1>
                <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl" data-testid="hero-subtitle">
                  {t.hero.subtitle}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-12">
                  <Button
                    size="lg"
                    className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-8 py-6 text-base"
                    onClick={() => navigate('/onboarding?plan=free')}
                    data-testid="hero-cta-primary"
                  >
                    {t.hero.cta}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium px-8 py-6 text-base"
                    onClick={() => navigate(currentLang === 'en' ? '/en/contact' : '/pt/contato')}
                    data-testid="hero-cta-secondary"
                  >
                    {t.hero.ctaSecondary}
                  </Button>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                  {t.stats.map((stat, index) => (
                    <div key={index} data-testid={`hero-stat-${index}`}>
                      <div className="text-2xl sm:text-3xl font-bold text-slate-900 font-chivo">{stat.value}</div>
                      <div className="text-sm text-slate-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Right Image */}
              <div className="relative lg:pl-8">
                <div className="relative">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/83adac59-fac1-46bd-96c9-e9f47abe68b4/images/b59f3bba9a9684b656d39b95ecc53d52eee23b1541ba95d9916fbfbf5249ee73.png"
                    alt="Security visualization"
                    className="w-full rounded-2xl"
                  />
                  {/* Floating card */}
                  <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-xl p-4 shadow-lg max-w-[200px]">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-sm font-semibold text-slate-900">100% Anônimo</span>
                    </div>
                    <p className="text-xs text-slate-500">Dados protegidos e criptografados</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Features */}
        <section className="border-y border-slate-200 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              {t.trust.features.slice(0, 4).map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Compliance Section */}
        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-2xl mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                {t.compliance.overline}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4 font-chivo" data-testid="compliance-title">
                {t.compliance.title}
              </h2>
              <p className="text-lg text-slate-600">
                {t.compliance.subtitle}
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.compliance.items.map((item, index) => {
                const Icon = getIcon(item.icon);
                return (
                  <div 
                    key={index}
                    className="group bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md hover:border-slate-300 transition-all"
                    data-testid={`compliance-card-${index}`}
                  >
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-50 transition-colors">
                      <Icon className="w-6 h-6 text-slate-700 group-hover:text-blue-600 transition-colors" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2 font-chivo">{item.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Features Section with Image */}
        <section className="py-20 lg:py-28 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Image */}
              <div className="order-2 lg:order-1">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/83adac59-fac1-46bd-96c9-e9f47abe68b4/images/2201cf981d01cdf1d4837f62b408477ab558187d670e9af59b0d906830822afe.png"
                  alt="Compliance features"
                  className="w-full rounded-2xl"
                />
              </div>
              
              {/* Content */}
              <div className="order-1 lg:order-2">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                  {t.features.overline}
                </p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-8 font-chivo" data-testid="features-title">
                  {t.features.title}
                </h2>
                
                <div className="space-y-6">
                  {t.features.items.map((item, index) => {
                    const Icon = getIcon(item.icon);
                    return (
                      <div 
                        key={index}
                        className="flex gap-4"
                        data-testid={`feature-item-${index}`}
                      >
                        <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-slate-700" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-slate-900 mb-1 font-chivo">{item.title}</h3>
                          <p className="text-slate-600">{item.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
                {t.pricing.overline}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4 font-chivo" data-testid="pricing-title">
                {t.pricing.title}
              </h2>
              <p className="text-lg text-slate-600">{t.pricing.subtitle}</p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.pricing.plans.map((plan, index) => (
                <div 
                  key={index}
                  className={`relative rounded-xl overflow-hidden ${
                    plan.enterprise 
                      ? 'bg-slate-900 text-white' 
                      : plan.popular 
                        ? 'bg-white border-2 border-blue-600' 
                        : 'bg-white border border-slate-200'
                  }`}
                  data-testid={`pricing-card-${index}`}
                >
                  {plan.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-blue-600 text-white text-xs font-bold py-2 text-center uppercase tracking-wider">
                      {currentLang === 'en' ? 'Most Popular' : 'Mais Popular'}
                    </div>
                  )}
                  <div className={`p-6 ${plan.popular ? 'pt-12' : ''}`}>
                    <h3 className={`text-xl font-bold mb-1 font-chivo ${plan.enterprise ? 'text-white' : 'text-slate-900'}`}>
                      {plan.name}
                    </h3>
                    <p className={`text-sm mb-4 ${plan.enterprise ? 'text-slate-400' : 'text-slate-500'}`}>
                      {plan.description}
                    </p>
                    <div className="mb-6">
                      <span className={`text-4xl font-bold font-chivo ${plan.enterprise ? 'text-white' : 'text-slate-900'}`}>
                        {plan.price}
                      </span>
                      <span className={plan.enterprise ? 'text-slate-400' : 'text-slate-500'}>
                        {plan.period}
                      </span>
                    </div>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, fIndex) => (
                        <li key={fIndex} className={`flex items-center gap-2 text-sm ${plan.enterprise ? 'text-slate-300' : 'text-slate-600'}`}>
                          <CheckCircle className={`w-4 h-4 flex-shrink-0 ${plan.enterprise ? 'text-blue-400' : 'text-blue-600'}`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={`w-full py-5 font-medium ${
                        plan.enterprise 
                          ? 'bg-white text-slate-900 hover:bg-slate-100 hover:text-slate-900' 
                          : plan.popular
                            ? 'bg-blue-600 text-white hover:bg-blue-700 hover:text-white'
                            : 'bg-slate-900 text-white hover:bg-slate-800 hover:text-white'
                      }`}
                      onClick={() => handleCheckout(index)}
                      disabled={checkoutLoading === index}
                      data-testid={`pricing-cta-${index}`}
                    >
                      {checkoutLoading === index ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                        </span>
                      ) : (
                        <>
                          {plan.cta}
                          <ArrowUpRight className="w-4 h-4 ml-1" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 lg:py-28 bg-slate-50">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-12 font-chivo tracking-tight" data-testid="faq-title">
              {t.faq.title}
            </h2>
            
            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white border border-slate-200 rounded-xl overflow-hidden"
                  data-testid={`faq-item-${index}`}
                >
                  <button
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="text-slate-900 font-medium pr-4">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 lg:py-32 bg-slate-900">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 font-chivo tracking-tight" data-testid="final-cta-title">
              {t.cta.title}
            </h2>
            <p className="text-lg text-slate-400 mb-8">{t.cta.subtitle}</p>
            <Button
              size="lg"
              className="bg-white text-slate-900 hover:bg-slate-100 hover:text-slate-900 font-medium px-10 py-6 text-base"
              onClick={() => navigate('/onboarding?plan=free')}
              data-testid="final-cta-button"
            >
              {t.cta.button}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#0B0F19] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-chivo">OMBUDNOW</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2025 OmbudNow. {t.footer.rights}
            </p>
            <div className="flex items-center gap-6">
              <Link to={currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade'} className="text-slate-500 hover:text-white text-sm transition-colors">
                {t.footer.privacy}
              </Link>
              <Link to={currentLang === 'en' ? '/en/terms-of-use' : '/pt/termos-de-uso'} className="text-slate-500 hover:text-white text-sm transition-colors">
                {t.footer.terms}
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Access Modal */}
      {showAccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-8 w-full max-w-md relative shadow-2xl border border-slate-200" data-testid="access-modal">
            <button 
              onClick={() => setShowAccess(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-2xl font-bold text-slate-900 mb-2 font-chivo">{t.modal.access.title}</h3>
            <p className="text-slate-600 mb-6">{t.modal.access.subtitle}</p>
            <form onSubmit={handleAccess}>
              <Input
                value={accessSlug}
                onChange={(e) => setAccessSlug(e.target.value)}
                placeholder={t.modal.access.placeholder}
                className="mb-4 border-slate-200"
                data-testid="access-slug-input"
              />
              <Button
                type="submit"
                className="w-full bg-slate-900 text-white hover:bg-slate-800 py-5 font-medium"
                disabled={loading}
                data-testid="access-submit"
              >
                {loading ? t.modal.access.accessing : t.modal.access.submit}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
